// CertificationTimeline.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import WorkIcon from "@mui/icons-material/Work";
import "./CertificationTimeline.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import useWindowResize from "../../../utils/WindowResize";
import HowThisCertificationHelp from "./HowThisCertificationHelp";
import BuildIcon from "@mui/icons-material/Build";
import VerifiedIcon from "@mui/icons-material/Verified";
import ShareIcon from "@mui/icons-material/Share";
import SearchIcon from "@mui/icons-material/Search";
import WorkshopFooterPage from "../Workshops/WorkshopFooter";
import CertificationProblemDetails from "./CertificationProblemDetails";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { getProblemStatementListAction } from "../../../actions/MyCertificates/certificatesAction";
import { getPricingOptionsAction } from "../../../actions/Pricing/pricingActions";
import { getUserRegion } from "../../../utils/change_time_format";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import EducationTopBar from "../topbar/EducationTopBar";

const CertificationTimeline = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const urlData = useParams();
  useEffect(() => {
    dispatch(getProblemStatementListAction(urlData?.id));
  }, []);
  useEffect(() => {
    if (!urlData?.id) return;
    dispatch(
      getPricingOptionsAction({
        eligibleItems: [{ itemType: "Certification", itemId: urlData?.id }],
        country: getUserRegion(),
      })
    );
  }, [urlData]);
  const { problemStatements } = useSelector(
    (state) => state.problemStatementListStore
  );
  const { pricingOption } = useSelector(
    (state) => state.getPricingOptionsStore
  );
  const { redirectUrl } = useSelector((state) => state.redirectUrlStore);

  const isSmallScreen = useWindowResize();
  const chooseProblemRef = useRef(null);
  const certificationHelpRef = useRef(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [hasClickedViewDetails, setHasClickedViewDetails] = useState(false);
  const [drawerDismissed, setDrawerDismissed] = useState(false);
  const [problemDrawerOpen, setProblemDrawerOpen] = useState(false);
  const [problemData, setProblemData] = useState({});
  const companies = [
    {
      name: "Google",
      position: "SDE1",
      icon: "https://www.google.com/favicon.ico",
    },
    {
      name: "Meta",
      position: "Full Stack Developer",
      icon: "https://static.xx.fbcdn.net/rsrc.php/yb/r/hLRJ1GG_y0J.ico",
    },
    {
      name: "Amazon",
      position: "Frontend Developer",
      icon: "https://www.amazon.com/favicon.ico",
    },
  ];

  const timelineSteps = [
    {
      icon: <BuildIcon />,
      title: "You Build a Real-World Project",
      description:
        "Every certification includes one — not toy problems, but practical builds.",
    },
    {
      icon: <VerifiedIcon />,
      title: "You Earn a Verified Certificate",
      description:
        "Each certificate is unique, shareable, and recruiter-verifiable.",
    },
    {
      icon: <ShareIcon />,
      title: "You Post It on LinkedIn & GitHub",
      description: "Instantly adds credibility and visibility to your name.",
    },
    {
      icon: <SearchIcon />,
      title: "Recruiters Can Now See Proof, Not Just Words",
      description: "Verified skills and code make you show up in searches.",
    },
  ];
  const recruiterReasons = [
    {
      emoji: "🔍",
      title: "They Trust Verifiable Skills",
      description:
        "Recruiters prefer candidates with visible proof of what they've built.",
    },
    {
      emoji: "💻",
      title: "They Can Check Your Code",
      description:
        "Each project is public — shows your thinking, not just your marks.",
    },
    {
      emoji: "💬",
      title: "They Find You Faster",
      description:
        'Certificates help your profile show in "React Developer" or "Node.js Intern" searches.',
    },
  ];
  const benefits = [
    {
      heading: "Stand Out in ATS Systems",
      subtext:
        "Your certification gets parsed and highlighted by applicant tracking systems, putting you ahead of non-certified candidates.",
    },
    {
      heading: "Verified Skills Badge",
      subtext:
        "Showcase project-based validation that proves you can deliver real solutions, not just pass multiple-choice tests.",
    },
    {
      heading: "LinkedIn Profile Boost",
      subtext:
        "Add a credential that recruiters actively search for when sourcing candidates for technical roles.",
    },
    {
      heading: "Fast-Track Interviews",
      subtext:
        "Certified candidates are 3x more likely to receive interview requests from hiring managers.",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShowDrawer(false);
            setDrawerDismissed(true);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (certificationHelpRef.current) {
      observer.observe(certificationHelpRef.current);
    }

    return () => {
      if (certificationHelpRef.current) {
        observer.unobserve(certificationHelpRef.current);
      }
    };
  }, []);

  // Show drawer after delay if conditions are met
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasClickedViewDetails && !drawerDismissed) {
        setShowDrawer(true);
      }
    }, 8000);

    return () => {
      clearTimeout(timer);
    };
  }, [hasClickedViewDetails, drawerDismissed]);

  useEffect(() => {
    if (redirectUrl?.url?.length > 0) {
      setProblemDrawerOpen(true);
      setProblemData(redirectUrl?.problemData);
      setHasClickedViewDetails(true);
      setDrawerDismissed(true);
      setShowDrawer(false);
    }
  }, [redirectUrl]);

  const scrollToChooseProblem = (offset = 100) => {
    if (!chooseProblemRef.current) return;

    const element = chooseProblemRef.current;
    const top = element.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: top - offset,
      behavior: "smooth",
    });
  };

  const handleViewDetailsClick = (data) => {
    setProblemData(data);
    setProblemDrawerOpen(true);
    setHasClickedViewDetails(true);
    setDrawerDismissed(true);
    setShowDrawer(false);
  };

  const handleDrawerButtonClick = () => {
    setShowDrawer(false);
    scrollToChooseProblem(120);
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
    setDrawerDismissed(true); // Mark as dismissed when closed
  };

  const renderTopContent = () => {
    return (
      <div>
        {problemStatements?.length > 0 && (
          <motion.div
            className="certificationTimeline-headerCard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="certificationTimeline-headerImage">
              <img
                src={problemStatements[0]?.certification?.image?.url}
                alt="Workspace"
              />
            </div>

            <div className="certificationTimeline-headerContent">
              <motion.div
                className="certificationTimeline-titleSection"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <h2 className="certificationTimeline-title">
                  {problemStatements[0]?.certification?.title}
                </h2>
                <p className="certificationTimeline-subtitle">
                  {problemStatements[0]?.certification?.subtitle}
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Timeline */}
        <motion.div
          className="certificationTimeline-timelineCard"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <div className="certificationTimeline-timeline">
            {/* First Checkpoint - Badge Earned */}

            {/* Second Checkpoint - In Progress with Pulse */}
            <motion.div
              className="certificationTimeline-timelineItem"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <div className="certificationTimeline-checkpointWrapper">
                <motion.div
                  className="certificationTimeline-checkpoint certificationTimeline-checkpointActive"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1, type: "spring", stiffness: 200 }}
                >
                  <div className="certificationTimeline-pulseRing"></div>
                  <DescriptionIcon className="certificationTimeline-iconActive" />
                </motion.div>
                <div className="certificationTimeline-line"></div>
              </div>

              <div className="certificationTimeline-content">
                <h3 className="certificationTimeline-itemTitle">
                  Complete the Certification
                </h3>
                <p className="certificationTimeline-description">
                  You’re just one project away from your verified LinkedIn
                  certificate.
                </p>
                <p
                  style={{ color: "#fff" }}
                  className="certificationTimeline-description"
                >
                  90% of learners who completed this got interview calls within
                  30 days.
                </p>
                <motion.button
                  className="certificationTimeline-shimmerButton"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToChooseProblem(120)}
                >
                  <span>Choose your Problem</span>
                </motion.button>
              </div>
            </motion.div>

            {/* Third Checkpoint - Career Opportunities */}
            <motion.div
              className="certificationTimeline-timelineItem"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <div className="certificationTimeline-checkpointWrapper">
                <motion.div
                  className="certificationTimeline-checkpoint certificationTimeline-checkpointPending"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
                >
                  <WorkIcon className="certificationTimeline-iconPending" />
                </motion.div>
              </div>

              <div className="certificationTimeline-content">
                <h3 className="certificationTimeline-itemTitle">
                  Career Opportunities
                </h3>

                <div className="certificationTimeline-companies">
                  {companies.map((company, index) => (
                    <motion.div
                      key={company.name}
                      className="certificationTimeline-companyCard"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.4 + index * 0.1, duration: 0.4 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <div className="certificationTimeline-companyIcon">
                        {company.name.charAt(0)}
                      </div>
                      <div className="certificationTimeline-companyInfo">
                        <p className="certificationTimeline-companyPosition">
                          {company.position}
                        </p>
                        <p className="certificationTimeline-companyName">
                          {company.name}
                        </p>
                      </div>
                    </motion.div>
                  ))}

                  <motion.div
                    className="certificationTimeline-moreCount"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.9, duration: 0.5 }}
                  >
                    +120 others
                  </motion.div>
                </div>

                <p className="certificationTimeline-bottomInfo">
                  More than 700+ students have aced their interviews and landed
                  internships or jobs.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <div>
      <EducationTopBar
        logo={
          <div
            onClick={() => history.push("/")}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <img
              className={isSmallScreen ? "mobile-logo" : "desktop-logo"}
              src="https://res.cloudinary.com/cqn/image/upload/v1740519381/white_acetechies_logo_vsdhp5.png"
              alt=""
            />
          </div>
        }
      />

      <div className="certificationTimeline-container">
        {/* Header Card */}
        {renderTopContent()}
        {/* New Challenge Section */}
        <motion.div
          className="certificationTimeline-challengeSection"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          ref={chooseProblemRef}
        >
          <div className="certificationTimeline-challengeHeader">
            <h2 className="certificationTimeline-challengeTitle">
              Choose a Challenge
            </h2>
            <p className="certificationTimeline-challengeSubtitle">
              Select a project that matches your skill level and start earning
              your certification
            </p>
          </div>

          <div className="certificationTimeline-carouselWrapper">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 10000, disableOnInteraction: false }}
              breakpoints={{
                640: {
                  slidesPerView: 1.5,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },
                1024: {
                  slidesPerView: 2.5,
                  spaceBetween: 24,
                },
              }}
              className="certificationTimeline-swiper"
            >
              {problemStatements
                ?.slice()
                ?.sort((a, b) => a?.order - b?.order)
                ?.map((problemStatement) => (
                  <SwiperSlide
                    style={{ height: `${isSmallScreen ? "52vh" : "72vh"}` }}
                    key={problemStatement?._id}
                  >
                    <motion.div
                      className="certificationTimeline-challengeCard"
                      whileHover={{ y: 0 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => handleViewDetailsClick(problemStatement)}
                    >
                      <div className="certificationTimeline-challengeImage">
                        <img
                          src={problemStatement?.image?.url}
                          alt={problemStatement?.image?.alt}
                        />
                        <div className="certificationTimeline-challengeOverlay"></div>
                      </div>

                      <div className="certificationTimeline-challengeContent">
                        <div className="certificationTimeline-challengeMeta">
                          <div className="certificationTimeline-metaItem">
                            <PersonIcon className="certificationTimeline-metaIcon" />
                            <span>{problemStatement?.totalRegistrations}</span>
                          </div>
                          <div className="certificationTimeline-metaItem">
                            <CalendarTodayIcon className="certificationTimeline-metaIcon" />
                            <span>
                              {problemStatement?.daysToComplete?.displayText}
                            </span>
                          </div>
                        </div>

                        <h3 className="certificationTimeline-challengeCardTitle">
                          {problemStatement?.title}
                        </h3>
                        <p className="certificationTimeline-challengeCardSubtitle">
                          {problemStatement?.subtitle}
                        </p>

                        <motion.button
                          className="certificationTimeline-viewDetailsButton"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() =>
                            handleViewDetailsClick(problemStatement)
                          }
                        >
                          View Details
                        </motion.button>
                      </div>
                    </motion.div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </motion.div>
        <p className="certificationTimeline-bottomInfo">
          More than 100+ students have been reached out by Recruiters for
          Internships or Jobs.
        </p>
        <div className="howHelp-container">
          {/* Header Section */}
          <motion.div
            className="howHelp-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="howHelp-title">
              How This Certification Helps You Land Your Next Internship or Job
            </h2>
            <p className="howHelp-subtitle">
              You're not just learning — you're building verifiable proof
              recruiters can see, trust, and act on.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            className="howHelp-benefitsGrid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="howHelp-benefitCard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
              >
                <h3 className="howHelp-benefitHeading">{benefit.heading}</h3>
                <p className="howHelp-benefitSubtext">{benefit.subtext}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Recruiter Visibility Timeline Section */}
          <motion.div
            className="howHelp-timelineSection"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <h3 className="howHelp-sectionTitle">
              Recruiter Visibility Timeline
            </h3>

            <div className="howHelp-timeline">
              {timelineSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="howHelp-timelineItem"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.15, duration: 0.5 }}
                >
                  <div className="howHelp-checkpointWrapper">
                    <motion.div
                      className="howHelp-checkpoint"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 1 + index * 0.15,
                        type: "spring",
                        stiffness: 200,
                      }}
                    >
                      {step.icon}
                    </motion.div>
                    {index !== timelineSteps.length - 1 && (
                      <div className="howHelp-line"></div>
                    )}
                  </div>

                  <div className="howHelp-timelineContent">
                    <h4 className="howHelp-timelineTitle">{step.title}</h4>
                    <p className="howHelp-timelineDescription">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Why Recruiters Care Section */}
          <motion.div
            className="howHelp-recruitersSection"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
          >
            <h3 className="howHelp-sectionTitle">Why Recruiters Care</h3>

            <div className="howHelp-recruitersGrid">
              {recruiterReasons.map((reason, index) => (
                <motion.div
                  key={index}
                  className="howHelp-recruiterCard"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 + index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="howHelp-recruiterEmoji">{reason.emoji}</div>
                  <h4 className="howHelp-recruiterTitle">{reason.title}</h4>
                  <p className="howHelp-recruiterDescription">
                    {reason.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
        <HowThisCertificationHelp
          open={showDrawer}
          onClose={handleDrawerClose}
          onButtonClick={handleDrawerButtonClick}
        />
        <CertificationProblemDetails
          open={problemDrawerOpen}
          onClose={() => setProblemDrawerOpen(false)}
          problemData={problemData}
          pricingData={pricingOption?.options?.[0]}
        />
        <div style={{ width: "100%" }}>
          <WorkshopFooterPage />
        </div>
      </div>
    </div>
  );
};

export default CertificationTimeline;
